import { Authenticated, CustomRoutes, Resource } from 'react-admin'
import { Route } from 'react-router-dom'
import { CssBaseline } from '@mui/material'
import auth from './resources/auth'

// Theme, Auth & Data
import theme from './themes'
import CustomLayout from './layouts'

import initAuthProvider from './providers/auth'
import initDataProvider from './providers/data'
import { i18nProvider } from './providers/localization'
// Resources
import UserProfile from './resources/auth/UserProfile'
import adminResource from './resources/admins'
import rolesPermissionsResource from './resources/roles-permissions'
import productsTireResource from './resources/products-tire'
import productsShockResource from './resources/products-shock'
import productsBrakeResource from './resources/products-brake'
import productsLubricantResource from './resources/products-lubricant'
import productsBatteryResource from './resources/products-battery'
import productsOtherResource from './resources/products-other'
import productBannersResource from './resources/product-banners'

import promotionsResource from './resources/promotions'
import servicesResource from './resources/services'
import homepageHeroBannersResource from './resources/homepage-hero-banners'
import homepagePartnerIconsResource from './resources/homepage-partner-icons'
import homepageContactUsResource from './resources/contact-us'
import NewsBlogSectionEdit from './resources/homepage-news-blog-section/EditNewsBlog'
import cataloguesResource from './resources/homepage-catalogues'
import JobOpportunityResource from './resources/job-opportunities'
import newsResource from './resources/news'
import CSRResource from './resources/csr'
import blogsResource from './resources/blogs'

// Icons
import AssignmentInd from '@mui/icons-material/AssignmentIndRounded'
import LockPersonIcon from '@mui/icons-material/LockPerson'
import NewspaperIcon from '@mui/icons-material/NewspaperRounded'
import CategoryIcon from '@mui/icons-material/CategoryRounded'
import PodcastsIcon from '@mui/icons-material/PodcastsRounded'
import UpdateIcon from '@mui/icons-material/Update'
import SourceRoundedIcon from '@mui/icons-material/SourceRounded'
import Star from '@mui/icons-material/Star'

import { RbacAdmin } from './components/ra-rbac'
import { createBrowserHistory as createHistory } from 'history'
import GlobalStyles from './themes/styles/globalStyles'
import NewsPreview from './resources/news/NewsPreview'
import BlogPreview from './resources/blogs/BlogPreview'
import CSRPreview from './resources/csr/CSRPreview'
import Dashboard from './resources/dashboard'
import ImportProduct from './resources/import-product/Import'
import portalMenusResource from './resources/homepage-portal-menus'
import PromotionPreview from './resources/promotions/PromotionPreview'
import promotionBannersResource from './resources/promotion-banners'
import productsBrandsResource from './resources/products-brands'
import carsTireResource from './resources/cars-tire'
import carsBattResource from './resources/cars-battery'
import carsBrakeResource from './resources/cars-brake'
import carsShockResource from './resources/cars-shock'
import ImportMasterData from './resources/import-master-data/Import'
import productsReviewsResource from './resources/products-reviews'
import JobOpportunityBanner from './resources/homepage-job-opportunities-banner/Edit'

const App = (): JSX.Element => {
  const authProvider = initAuthProvider()
  const dataProvider = initDataProvider()
  const history = createHistory()
  return (
    <>
      <CssBaseline />
      <GlobalStyles />
      <RbacAdmin
        theme={theme}
        loginPage={auth.login}
        layout={CustomLayout}
        dashboard={Dashboard}
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        history={history}
        disableTelemetry
      >
        <Resource
          name='hero-banners'
          options={{ label: 'HomePage Hero Banners Management' }}
          icon={UpdateIcon}
          {...homepageHeroBannersResource}
        />
        <Resource
          name='portal-menus'
          options={{ label: 'Product Lubricant Management' }}
          icon={SourceRoundedIcon}
          {...portalMenusResource}
        />
        <Resource
          name='partner-icons'
          options={{ label: 'HomePage Partner Icons Management' }}
          icon={UpdateIcon}
          {...homepagePartnerIconsResource}
        />
        <Resource
          name='contact-us'
          options={{ label: 'HomePage Contact Us Management' }}
          icon={UpdateIcon}
          {...homepageContactUsResource}
        />
        <Resource
          name='catalogues'
          options={{ label: 'HomePage Catalogues Management' }}
          icon={UpdateIcon}
          {...cataloguesResource}
        />
        <Resource
          name='job-opportunities'
          options={{ label: 'Job Opportunities Management' }}
          icon={UpdateIcon}
          {...JobOpportunityResource}
        />
        <Resource
          name='products-tire'
          options={{ label: 'Product Tire Management' }}
          icon={SourceRoundedIcon}
          {...productsTireResource}
        />
        <Resource
          name='products-shock'
          options={{ label: 'Product Shock Management' }}
          icon={SourceRoundedIcon}
          {...productsShockResource}
        />
        <Resource
          name='products-brake'
          options={{ label: 'Product Brake Management' }}
          icon={SourceRoundedIcon}
          {...productsBrakeResource}
        />
        <Resource
          name='products-lubricant'
          options={{ label: 'Product Lubricant Management' }}
          icon={SourceRoundedIcon}
          {...productsLubricantResource}
        />
        <Resource
          name='products-battery'
          options={{ label: 'Product Battery Management' }}
          icon={SourceRoundedIcon}
          {...productsBatteryResource}
        />
        <Resource
          name='products-other'
          options={{ label: 'Product Other Management' }}
          icon={SourceRoundedIcon}
          {...productsOtherResource}
        />
        <Resource
          name='products-reviews'
          options={{ label: 'Product Review Management' }}
          icon={Star}
          {...productsReviewsResource}
        />
        <Resource
          name='products-banners'
          options={{ label: 'Product Banner Management' }}
          icon={SourceRoundedIcon}
          {...productBannersResource}
        />
        <Resource
          name='promotions'
          options={{ label: 'Promotion Management' }}
          icon={PodcastsIcon}
          {...promotionsResource}
        />
        <Resource
          name='promotions-banners'
          options={{ label: 'Promotion Banner Management' }}
          icon={PodcastsIcon}
          {...promotionBannersResource}
        />
        <Resource
          name='news'
          options={{ label: 'News Management' }}
          icon={NewspaperIcon}
          {...newsResource}
        />
        <Resource
          name='csr'
          options={{ label: 'CSR Management' }}
          icon={NewspaperIcon}
          {...CSRResource}
        />
        <Resource
          name='blogs'
          options={{ label: 'Blog Management' }}
          icon={NewspaperIcon}
          {...blogsResource}
        />
        <Resource
          name='services'
          options={{ label: 'Service Management' }}
          icon={CategoryIcon}
          {...servicesResource}
        />
        <Resource
          name='admins'
          options={{ label: 'Admin Management' }}
          icon={AssignmentInd}
          {...adminResource}
        />
        <Resource
          name='roles'
          options={{ label: 'Roles & Permissions' }}
          icon={LockPersonIcon}
          {...rolesPermissionsResource}
        />
        <Resource
          name='brand-tire'
          options={{ label: 'Brands Tire' }}
          {...productsBrandsResource}
        />
        <Resource
          name='brand-brake'
          options={{ label: 'Brands Brake' }}
          {...productsBrandsResource}
        />
        <Resource
          name='brand-battery'
          options={{ label: 'Brands Battery' }}
          {...productsBrandsResource}
        />
        <Resource
          name='brand-shock'
          options={{ label: 'Brands Shock' }}
          {...productsBrandsResource}
        />
        <Resource
          name='brand-lubricant'
          options={{ label: 'Brands Lubricant' }}
          {...productsBrandsResource}
        />
        <Resource
          name='brand-other'
          options={{ label: 'Brands Other' }}
          {...productsBrandsResource}
        />
        <Resource name='cars-tire' options={{ label: 'Cars Tire' }} {...carsTireResource} />
        <Resource name='cars-brake' options={{ label: 'Cars Brake' }} {...carsBrakeResource} />
        <Resource name='cars-shock' options={{ label: 'Cars Shock' }} {...carsShockResource} />
        <Resource name='cars-battery' options={{ label: 'Cars Battery' }} {...carsBattResource} />
        <CustomRoutes>
          <Route
            path='/profile/'
            element={
              <Authenticated requireAuth>
                <UserProfile />
              </Authenticated>
            }
          />
          <Route
            path='/import-product/:productType'
            element={
              <Authenticated requireAuth>
                <ImportProduct />
              </Authenticated>
            }
          />
          <Route
            path='/import-master-data/:importType/:productType'
            element={
              <Authenticated requireAuth>
                <ImportMasterData />
              </Authenticated>
            }
          />
          <Route
            path='/news-blog-section'
            element={
              <Authenticated requireAuth>
                <NewsBlogSectionEdit />
              </Authenticated>
            }
          />
          <Route
            path='/job-opportunity-banner'
            element={
              <Authenticated requireAuth>
                <JobOpportunityBanner />
              </Authenticated>
            }
          />
        </CustomRoutes>
        <CustomRoutes noLayout>
          <Route path='/news/:id/preview' element={<NewsPreview />} />
          <Route path='/blogs/:id/preview' element={<BlogPreview />} />
          <Route path='/promotions/:id/preview' element={<PromotionPreview />} />
          <Route path='/csr/:id/preview' element={<CSRPreview />} />
        </CustomRoutes>
      </RbacAdmin>
    </>
  )
}

export default App
